import { useEffect, useState } from "react";
import Head from "next/head";
import { connect } from "react-redux";
import getConfig from "next/config";

import Breadcrumbs from "@components/Breadcrumbs";
import { makeApiCall } from "@lib/api/helpers";
import { getCmsBlocksApi, getCustomerVehiclesApi } from "@lib/api";
import RCAForm from "@containers/RCA/RCAForm";
import { addAlert } from "@store/app/actions";
import Loading from "@components/Loading";
import RCALogin from "@containers/RCA/RCALogin";
import RCAStepNav from "@containers/RCA/RCAStepNav";
import RCASidebar from "@containers/RCA/RCASidebar";
import RCASavedInfo from "@containers/RCA/RCASavedInfo";
import ErrorPage from "@containers/ErrorPage";

const {
  publicRuntimeConfig: {
    settings: { websiteName },
  },
} = getConfig();

const RcaPage = ({ user, addAlert, cmsBlockData }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [apiData, setApiData] = useState({ cmsBlockData });

  const { isInitial, isLoggedIn } = user;
  const hasSavedVehicles =
    apiData.savedVehicles && Object.keys(apiData.savedVehicles).length > 0;

  const handleStepChange = (step) => {
    setCurrentStep(step);

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleApiDataUpdate = (data) => {
    setApiData((prev) => ({ ...prev, ...data }));
  };

  const handleLoadingStateChange = (loadingState) => {
    setIsLoading(loadingState);
  };

  useEffect(() => {
    const fetchSavedVehicles = async () => {
      if (!isLoading) {
        setIsLoading(true);
      }

      const response = await makeApiCall({
        apiCall: getCustomerVehiclesApi,
        returnResponse: true,
        formatResponse: (response) => response.data.data,
        preFail: () =>
          addAlert({
            message: "Eroare la încărcarea vehiculelor salvate.",
            variant: "error",
          }),
      });

      if (response?.items?.length) {
        const savedVehicles = Object.fromEntries(
          response.items.map((item) => [item.id, item])
        );

        handleApiDataUpdate({ savedVehicles });
      } else {
        setCurrentStep(2);
      }

      setIsLoading(false);
    };

    if (isLoggedIn && !apiData.savedVehicles) {
      fetchSavedVehicles();
    }
  }, [isLoggedIn]);

  if (websiteName === "bricostore" || websiteName === "mediagalaxy") {
    return <ErrorPage layout="404" />;
  }

  return (
    <>
      <Head>
        <title>Asigurare RCA</title>

        <meta
          name="description"
          content="Asigurare RCA online - completeaza formularul si obtine oferta instantaneu"
        />
      </Head>

      <Breadcrumbs
        breadcrumbs={[
          {
            id: 1,
            level: 99,
            name: "RCA",
            url: "/asigurari-rca",
          },
        ]}
      />

      <div className="py-[24px]">
        {isLoggedIn && (
          <RCAStepNav
            currentStep={currentStep}
            onStepChange={handleStepChange}
            hasSavedVehicles={hasSavedVehicles}
          />
        )}

        <div className="flex flex-col lg:flex-row lg:items-start gap-[26px]">
          <div className="bg-[#F5FBF4] mt-[36px] py-[20px] px-[15px] lg:px-[20px] max-h-fit rounded-tr-[4px] rounded-bl-[4px] lg:w-[60%] relative min-h-[200px]">
            {!isLoggedIn && !isInitial && <RCALogin />}

            {isLoggedIn && currentStep === 1 && hasSavedVehicles && (
              <RCASavedInfo
                onStepChange={handleStepChange}
                onApiDataUpdate={handleApiDataUpdate}
                apiData={apiData}
                onLoadingStateChange={handleLoadingStateChange}
                isLoading={isLoading}
              />
            )}

            {isLoggedIn && currentStep > 1 && (
              <RCAForm
                currentStep={currentStep}
                apiData={apiData}
                onApiDataUpdate={handleApiDataUpdate}
                onStepChange={handleStepChange}
                isFetching={isLoading}
                onLoadingStateChange={handleLoadingStateChange}
              />
            )}

            {isLoading && (
              <div className="absolute flex items-center justify-center inset-0 bg-white overflow-hidden opacity-50 resetLoading min-h-[200px]">
                <Loading height={100} hasMarginBottom={false} />
              </div>
            )}
          </div>

          <div className="lg:w-[40%]">
            <RCASidebar
              cmsBlockData={apiData.cmsBlockData}
              onApiDataUpdate={handleApiDataUpdate}
            />
          </div>
        </div>
      </div>
    </>
  );
};

RcaPage.pageLayout = "default";

RcaPage.getInitialProps = async () => {
  const response = await makeApiCall({
    apiCall: getCmsBlocksApi,
    formatResponse: (response) => response.data,
    apiArgs: [["faq_asigurare-rca", "info_rca"]],
  });

  if (response?.blocks?.length) {
    return {
      cmsBlockData: response.blocks,
    };
  }
};

export default connect(
  (state) => ({
    user: state.user,
  }),
  { addAlert }
)(RcaPage);
